import api from "./client";

// ============================================
// TYPES
// ============================================

export type VariableScope = 'parameter' | 'variable';
export type VariableType = 'string' | 'number' | 'boolean' | 'select' | 'multiselect' | 'json' | 'reference';

export interface ValidationRules {
  required?: boolean;
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  enum?: string[];
}

export interface ArchitectureVariable {
  id: string;
  architecture_id: string;
  name: string;
  display_name?: string;
  description?: string;
  scope: VariableScope;
  type: VariableType;
  default_value?: { value?: any; expression?: string };
  validation_rules?: ValidationRules;
  category?: string;
  display_order?: number;
  is_sensitive?: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateVariableData {
  name: string;
  displayName?: string;
  description?: string;
  scope?: VariableScope;
  type: VariableType;
  defaultValue?: { value?: any; expression?: string };
  validationRules?: ValidationRules;
  category?: string;
  displayOrder?: number;
  isSensitive?: boolean;
}

export interface UpdateVariableData {
  displayName?: string;
  description?: string;
  type?: VariableType;
  defaultValue?: { value?: any; expression?: string };
  validationRules?: ValidationRules;
  category?: string;
  displayOrder?: number;
  isSensitive?: boolean;
}

export interface EnvironmentOverride {
  id: string;
  environment_id: string;
  variable_id: string;
  value: any;
  created_at: string;
  updated_at: string;
  variable?: ArchitectureVariable;
}

export interface ResolvedParameter {
  name: string;
  value: any;
  source: 'default' | 'environment_override' | 'execution_override';
  type: VariableType;
  displayName?: string;
  description?: string;
  validationRules?: ValidationRules;
  isSensitive?: boolean;
}

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

export interface ParameterValidation {
  valid: boolean;
  parameterErrors: Record<string, string[]>;
}

// ============================================
// ARCHITECTURE VARIABLES CRUD
// ============================================

export async function getArchitectureVariables(
  architectureId: string,
  scope?: VariableScope
): Promise<ArchitectureVariable[]> {
  const params = scope ? { scope } : {};
  const response = await api.get(`/api/architectures/${architectureId}/variables`, { params });
  return response.data.variables || response.data;
}

export async function getVariable(
  architectureId: string,
  variableId: string
): Promise<ArchitectureVariable> {
  const response = await api.get(`/api/architectures/${architectureId}/variables/${variableId}`);
  return response.data.variable || response.data;
}

export async function createVariable(
  architectureId: string,
  data: CreateVariableData
): Promise<ArchitectureVariable> {
  const response = await api.post(`/api/architectures/${architectureId}/variables`, data);
  return response.data.variable || response.data;
}

export async function updateVariable(
  architectureId: string,
  variableId: string,
  data: UpdateVariableData
): Promise<ArchitectureVariable> {
  const response = await api.put(`/api/architectures/${architectureId}/variables/${variableId}`, data);
  return response.data.variable || response.data;
}

export async function deleteVariable(
  architectureId: string,
  variableId: string
): Promise<void> {
  await api.delete(`/api/architectures/${architectureId}/variables/${variableId}`);
}

// ============================================
// ENVIRONMENT OVERRIDES
// ============================================

export async function getEnvironmentOverrides(
  environmentId: string
): Promise<EnvironmentOverride[]> {
  const response = await api.get(`/api/environments/${environmentId}/variable-overrides`);
  return response.data.overrides || response.data;
}

export async function setEnvironmentOverride(
  environmentId: string,
  variableId: string,
  value: any
): Promise<EnvironmentOverride> {
  const response = await api.post(`/api/environments/${environmentId}/variables/${variableId}/override`, { value });
  return response.data.override || response.data;
}

export async function removeEnvironmentOverride(
  environmentId: string,
  variableId: string
): Promise<void> {
  await api.delete(`/api/environments/${environmentId}/variables/${variableId}/override`);
}

// ============================================
// PARAMETER RESOLUTION
// ============================================

export async function previewParameters(
  architectureId: string,
  environmentId?: string,
  overrides?: Record<string, any>
): Promise<{
  parameters: ResolvedParameter[];
  validation: ParameterValidation;
}> {
  const response = await api.post('/api/executions/preview-parameters', {
    architectureId,
    environmentId,
    overrides
  });
  return response.data;
}

// ============================================
// TERRAFORM PREVIEW
// ============================================

export async function getTerraformPreview(
  architectureId: string,
  environmentId?: string
): Promise<{
  terraform_code: string;
  parameters: ResolvedParameter[];
}> {
  const params = environmentId ? { environmentId } : {};
  const response = await api.get(`/api/architectures/${architectureId}/terraform-preview`, { params });
  return response.data;
}

// ============================================
// VALIDATION
// ============================================

export async function validateValue(
  value: any,
  validationRules: ValidationRules,
  type: VariableType
): Promise<ValidationResult> {
  const response = await api.post('/api/variables/validate', {
    value,
    validationRules,
    type
  });
  return response.data;
}
